import logger from "../utils/logger";
import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from '../components/ui/dialog';
import { ListTodo, Plus, Bug, AlertCircle, Clock, CheckCircle, User, Calendar, LayoutGrid, List } from 'lucide-react';

import API_URL from '../utils/api';

const KANBAN_COLUMNS = [
    { id: 'todo', label: 'To Do', color: 'border-t-slate-400', bg: 'bg-slate-50' },
    { id: 'in_progress', label: 'In Progress', color: 'border-t-[var(--brand-primary)]', bg: 'bg-blue-50/30' },
    { id: 'in_review', label: 'In Review', color: 'border-t-purple-500', bg: 'bg-purple-50/30' },
    { id: 'completed', label: 'Completed', color: 'border-t-green-500', bg: 'bg-green-50/30' },
];

const Tasks = () => {
    const { user, canManage } = useAuth();
    const [tasks, setTasks] = useState([]);
    const [projects, setProjects] = useState([]);
    const [employees, setEmployees] = useState([]);
    const [summary, setSummary] = useState(null);
    const [loading, setLoading] = useState(true);
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [viewMode, setViewMode] = useState('list');
    const [filterStatus, setFilterStatus] = useState('');
    const [filterProject, setFilterProject] = useState('');
    const [formData, setFormData] = useState({
        title: '', description: '', project_id: '', task_type: 'task', priority: 'medium', assigned_to: '', due_date: ''
    });

    useEffect(() => { fetchData(); }, [filterStatus, filterProject]);

    const fetchData = async () => {
        try {
            let url = `${API_URL}/api/tasks`;
            const params = [];
            if (filterStatus) params.push(`status=${filterStatus}`);
            if (filterProject) params.push(`project_id=${filterProject}`);
            if (params.length) url += `?${params.join('&')}`;

            const [tasksRes, projectsRes, empRes, summaryRes] = await Promise.all([
                axios.get(url, { withCredentials: true }),
                axios.get(`${API_URL}/api/projects`, { withCredentials: true }),
                axios.get(`${API_URL}/api/employees`, { withCredentials: true }),
                axios.get(`${API_URL}/api/tasks/dashboard/summary`, { withCredentials: true })
            ]);
            setTasks(tasksRes.data);
            setProjects(projectsRes.data);
            setEmployees(empRes.data.filter(e => e.role !== 'admin'));
            setSummary(summaryRes.data);
        } catch (error) {
            logger.error('Error:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            const payload = { ...formData, due_date: formData.due_date ? new Date(formData.due_date).toISOString() : null };
            await axios.post(`${API_URL}/api/tasks`, payload, { withCredentials: true });
            setIsDialogOpen(false);
            setFormData({ title: '', description: '', project_id: '', task_type: 'task', priority: 'medium', assigned_to: '', due_date: '' });
            fetchData();
        } catch (error) {
            alert(error.response?.data?.detail || 'Error creating task');
        }
    };

    const updateTaskStatus = async (taskId, newStatus) => {
        try {
            await axios.put(`${API_URL}/api/tasks/${taskId}`, { status: newStatus }, { withCredentials: true });
            fetchData();
        } catch (error) {
            alert(error.response?.data?.detail || 'Error updating task');
        }
    };

    const getTypeIcon = (type) => {
        if (type === 'bug') return <Bug className="h-4 w-4 text-red-600" />;
        return <ListTodo className="h-4 w-4 text-brand" />;
    };

    const getPriorityColor = (priority) => {
        const colors = { low: 'bg-slate-100 text-slate-800', medium: 'bg-blue-100 text-blue-800', high: 'bg-amber-100 text-amber-800', critical: 'bg-red-100 text-red-800' };
        return colors[priority] || colors.medium;
    };

    const getStatusColor = (status) => {
        const colors = { todo: 'bg-slate-100 text-slate-800', in_progress: 'bg-blue-100 text-blue-800', in_review: 'bg-purple-100 text-purple-800', completed: 'bg-green-100 text-green-800', on_hold: 'bg-amber-100 text-amber-800' };
        return colors[status] || colors.todo;
    };

    if (loading) return <div className="flex items-center justify-center h-64"><div className="text-slate-500">Loading tasks...</div></div>;

    return (
        <div className="space-y-6" data-testid="tasks-page">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h1 className="text-3xl font-black text-slate-900 tracking-tight" style={{ fontFamily: 'Chivo, sans-serif' }}>Tasks</h1>
                    <p className="text-slate-500 mt-1">Manage tasks and track progress</p>
                </div>
                <div className="flex gap-2">
                    <div className="flex border border-slate-200">
                        <Button variant={viewMode === 'list' ? 'default' : 'ghost'} size="sm" className="rounded-none" onClick={() => setViewMode('list')} data-testid="tasks-list-view">
                            <List className="h-4 w-4 mr-1" />List
                        </Button>
                        <Button variant={viewMode === 'kanban' ? 'default' : 'ghost'} size="sm" className="rounded-none" onClick={() => setViewMode('kanban')} data-testid="tasks-kanban-view">
                            <LayoutGrid className="h-4 w-4 mr-1" />Kanban
                        </Button>
                    </div>
                    <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
                        <DialogTrigger asChild>
                            <Button className="bg-brand hover:bg-brand rounded-none" data-testid="add-task-btn"><Plus className="h-4 w-4 mr-2" />New Task</Button>
                        </DialogTrigger>
                    <DialogContent className="rounded-none max-w-lg">
                        <DialogHeader><DialogTitle className="text-xl font-bold" style={{ fontFamily: 'Chivo, sans-serif' }}>Create Task</DialogTitle>
                            <DialogDescription className="text-xs text-slate-500">Add a new task to the project with assignee and due date.</DialogDescription></DialogHeader>
                        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                            <div>
                                <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Title</Label>
                                <Input value={formData.title} onChange={(e) => setFormData({...formData, title: e.target.value})} className="rounded-none mt-1" required data-testid="task-title-input" />
                            </div>
                            <div>
                                <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Project</Label>
                                <Select value={formData.project_id} onValueChange={(v) => setFormData({...formData, project_id: v})}>
                                    <SelectTrigger className="rounded-none mt-1" data-testid="task-project-select"><SelectValue placeholder="Select project" /></SelectTrigger>
                                    <SelectContent className="rounded-none">{projects.map(p => <SelectItem key={p._id} value={p._id}>{p.name}</SelectItem>)}</SelectContent>
                                </Select>
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Type</Label>
                                    <Select value={formData.task_type} onValueChange={(v) => setFormData({...formData, task_type: v})}>
                                        <SelectTrigger className="rounded-none mt-1"><SelectValue /></SelectTrigger>
                                        <SelectContent className="rounded-none">
                                            <SelectItem value="task">Task</SelectItem>
                                            <SelectItem value="bug">Bug</SelectItem>
                                            <SelectItem value="feature">Feature</SelectItem>
                                            <SelectItem value="improvement">Improvement</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div>
                                    <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Priority</Label>
                                    <Select value={formData.priority} onValueChange={(v) => setFormData({...formData, priority: v})}>
                                        <SelectTrigger className="rounded-none mt-1"><SelectValue /></SelectTrigger>
                                        <SelectContent className="rounded-none">
                                            <SelectItem value="low">Low</SelectItem>
                                            <SelectItem value="medium">Medium</SelectItem>
                                            <SelectItem value="high">High</SelectItem>
                                            <SelectItem value="critical">Critical</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Assign To</Label>
                                    <Select value={formData.assigned_to} onValueChange={(v) => setFormData({...formData, assigned_to: v})}>
                                        <SelectTrigger className="rounded-none mt-1"><SelectValue placeholder="Select employee" /></SelectTrigger>
                                        <SelectContent className="rounded-none">{employees.map(e => <SelectItem key={e._id} value={e._id}>{e.name}</SelectItem>)}</SelectContent>
                                    </Select>
                                </div>
                                <div>
                                    <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Due Date</Label>
                                    <Input type="date" value={formData.due_date} onChange={(e) => setFormData({...formData, due_date: e.target.value})} className="rounded-none mt-1" />
                                </div>
                            </div>
                            <div>
                                <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Description</Label>
                                <Textarea value={formData.description} onChange={(e) => setFormData({...formData, description: e.target.value})} className="rounded-none mt-1" rows={3} />
                            </div>
                            <div className="flex justify-end gap-3 pt-4">
                                <Button type="button" variant="outline" className="rounded-none" onClick={() => setIsDialogOpen(false)}>Cancel</Button>
                                <Button type="submit" className="bg-brand hover:bg-brand rounded-none" data-testid="task-save-btn">Create Task</Button>
                            </div>
                        </form>
                    </DialogContent>
                </Dialog>
                </div>
            </div>

            {summary && (
                <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-4">
                    {[
                        { label: 'Total',       value: summary.total,        stripe: 'bg-gradient-to-r from-slate-500 to-slate-600',     tint: 'from-slate-50/60',   color: 'text-slate-900' },
                        { label: 'To Do',       value: summary.todo,         stripe: 'bg-gradient-to-r from-slate-400 to-slate-500',     tint: 'from-slate-50/60',   color: 'text-slate-700' },
                        { label: 'In Progress', value: summary.in_progress,  stripe: 'bg-gradient-to-r from-[#2563eb] to-[#3b82f6]',     tint: 'from-blue-50/60',    color: 'text-[#2563eb]' },
                        { label: 'In Review',   value: summary.in_review,    stripe: 'bg-gradient-to-r from-violet-500 to-fuchsia-500',  tint: 'from-violet-50/60',  color: 'text-violet-600' },
                        { label: 'Completed',   value: summary.completed,    stripe: 'bg-gradient-to-r from-emerald-500 to-teal-500',    tint: 'from-emerald-50/60', color: 'text-emerald-600' },
                        { label: 'Bugs',        value: summary.bugs,         stripe: 'bg-gradient-to-r from-rose-500 to-red-500',        tint: 'from-rose-50/60',    color: 'text-rose-600' },
                        { label: 'Critical',    value: summary.critical,     stripe: 'bg-gradient-to-r from-red-500 to-orange-600',      tint: 'from-red-50/60',     color: 'text-red-600' },
                        { label: 'Overdue',     value: summary.overdue,      stripe: 'bg-gradient-to-r from-amber-500 to-orange-500',    tint: 'from-amber-50/60',   color: 'text-amber-600' }
                    ].map((stat) => (
                        <Card key={stat.label} className={`relative rounded-xl border border-slate-200 bg-gradient-to-br ${stat.tint} to-white overflow-hidden transition-all hover:shadow-md hover:-translate-y-0.5`}>
                            <div className={`absolute top-0 left-0 h-1 w-full ${stat.stripe}`} />
                            <CardContent className="p-4 text-center">
                                <p className="text-[10px] font-bold uppercase tracking-[0.12em] text-slate-500">{stat.label}</p>
                                <p className={`text-2xl font-black mt-1 tabular-nums ${stat.color}`} style={{ fontFamily: 'Chivo, sans-serif' }}>{stat.value}</p>
                            </CardContent>
                        </Card>
                    ))}
                </div>
            )}

            {viewMode === 'list' && (
            <Card className="rounded-none border-slate-200">
                <CardHeader className="border-b border-slate-200">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                        <CardTitle className="text-lg font-bold flex items-center gap-2" style={{ fontFamily: 'Chivo, sans-serif' }}>
                            <ListTodo className="h-5 w-5 text-brand" />All Tasks
                        </CardTitle>
                        <div className="flex gap-2">
                            <Select value={filterProject || "all"} onValueChange={(v) => setFilterProject(v === "all" ? "" : v)}>
                                <SelectTrigger className="w-[150px] rounded-none"><SelectValue placeholder="All Projects" /></SelectTrigger>
                                <SelectContent className="rounded-none">
                                    <SelectItem value="all">All Projects</SelectItem>
                                    {projects.map(p => <SelectItem key={p._id} value={p._id}>{p.name}</SelectItem>)}
                                </SelectContent>
                            </Select>
                            <Select value={filterStatus || "all"} onValueChange={(v) => setFilterStatus(v === "all" ? "" : v)}>
                                <SelectTrigger className="w-[140px] rounded-none"><SelectValue placeholder="All Status" /></SelectTrigger>
                                <SelectContent className="rounded-none">
                                    <SelectItem value="all">All Status</SelectItem>
                                    <SelectItem value="todo">To Do</SelectItem>
                                    <SelectItem value="in_progress">In Progress</SelectItem>
                                    <SelectItem value="in_review">In Review</SelectItem>
                                    <SelectItem value="completed">Completed</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                    </div>
                </CardHeader>
                <CardContent className="p-0">
                    <div className="divide-y divide-slate-200">
                        {tasks.map((task) => (
                            <div key={task._id} className="p-4 hover:bg-slate-50 flex items-start gap-4" data-testid={`task-row-${task._id}`}>
                                <div className="flex-shrink-0 pt-1">{getTypeIcon(task.task_type)}</div>
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-start justify-between gap-2">
                                        <div>
                                            <p className="font-medium text-slate-900">{task.title}</p>
                                            <p className="text-sm text-slate-500">{task.task_code}</p>
                                        </div>
                                        <div className="flex items-center gap-2 flex-shrink-0">
                                            <Badge className={`rounded-none ${getPriorityColor(task.priority)}`}>{task.priority}</Badge>
                                            <Select value={task.status} onValueChange={(v) => updateTaskStatus(task._id, v)}>
                                                <SelectTrigger className={`w-[120px] h-7 rounded-none text-xs ${getStatusColor(task.status)}`}><SelectValue /></SelectTrigger>
                                                <SelectContent className="rounded-none">
                                                    <SelectItem value="todo">To Do</SelectItem>
                                                    <SelectItem value="in_progress">In Progress</SelectItem>
                                                    <SelectItem value="in_review">In Review</SelectItem>
                                                    <SelectItem value="completed">Completed</SelectItem>
                                                    <SelectItem value="on_hold">On Hold</SelectItem>
                                                </SelectContent>
                                            </Select>
                                        </div>
                                    </div>
                                    {task.description && <p className="text-sm text-slate-600 mt-1 line-clamp-1">{task.description}</p>}
                                    <div className="flex items-center gap-4 mt-2 text-sm text-slate-500">
                                        {task.assigned_to_name && (
                                            <div className="flex items-center gap-1"><User className="h-3 w-3" />{task.assigned_to_name}</div>
                                        )}
                                        {task.due_date && (
                                            <div className="flex items-center gap-1"><Calendar className="h-3 w-3" />{new Date(task.due_date).toLocaleDateString()}</div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        ))}
                        {tasks.length === 0 && <div className="p-8 text-center text-slate-500">No tasks found</div>}
                    </div>
                </CardContent>
            </Card>
            )}

            {viewMode === 'kanban' && (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="kanban-board">
                    {KANBAN_COLUMNS.map(col => {
                        const colTasks = tasks.filter(t => t.status === col.id);
                        return (
                            <div key={col.id} className={`border border-slate-200 ${col.bg} rounded-none`}>
                                <div className={`p-3 border-t-4 ${col.color} border-b border-slate-200`}>
                                    <div className="flex items-center justify-between">
                                        <h3 className="text-sm font-bold text-slate-700">{col.label}</h3>
                                        <Badge className="rounded-none bg-white text-slate-600 border border-slate-200">{colTasks.length}</Badge>
                                    </div>
                                </div>
                                <div className="p-2 space-y-2 min-h-[200px]">
                                    {colTasks.map(task => (
                                        <div key={task._id} className="bg-white border border-slate-200 p-3 hover:shadow-md transition-shadow cursor-pointer" data-testid={`kanban-card-${task._id}`}>
                                            <div className="flex items-start justify-between gap-2 mb-2">
                                                <div className="flex items-center gap-1.5">
                                                    {getTypeIcon(task.task_type)}
                                                    <span className="text-[10px] text-slate-400 font-mono">{task.task_code}</span>
                                                </div>
                                                <Badge className={`rounded-none text-[10px] ${getPriorityColor(task.priority)}`}>{task.priority}</Badge>
                                            </div>
                                            <p className="text-sm font-medium text-slate-900 line-clamp-2">{task.title}</p>
                                            {task.description && <p className="text-xs text-slate-500 mt-1 line-clamp-1">{task.description}</p>}
                                            <div className="flex items-center justify-between mt-3 pt-2 border-t border-slate-100">
                                                {task.assigned_to_name ? (
                                                    <div className="flex items-center gap-1 text-xs text-slate-500"><User className="h-3 w-3" />{task.assigned_to_name}</div>
                                                ) : <span />}
                                                {task.due_date && <span className="text-[10px] text-slate-400">{new Date(task.due_date).toLocaleDateString()}</span>}
                                            </div>
                                            <div className="mt-2">
                                                <Select value={task.status} onValueChange={(v) => updateTaskStatus(task._id, v)}>
                                                    <SelectTrigger className="h-6 rounded-none text-[10px] w-full"><SelectValue /></SelectTrigger>
                                                    <SelectContent className="rounded-none">
                                                        <SelectItem value="todo">To Do</SelectItem>
                                                        <SelectItem value="in_progress">In Progress</SelectItem>
                                                        <SelectItem value="in_review">In Review</SelectItem>
                                                        <SelectItem value="completed">Completed</SelectItem>
                                                    </SelectContent>
                                                </Select>
                                            </div>
                                        </div>
                                    ))}
                                    {colTasks.length === 0 && <div className="text-center text-xs text-slate-400 py-8">No tasks</div>}
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

export default Tasks;
